/**
 * Alert Component
 * 
 * Displays important messages to the user.
 * 
 * @module components/ui/Alert
 */

import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle, Info, XCircle, X } from 'lucide-react';

// =============================================================================
// Variants
// =============================================================================

const alertVariants = cva(
  'relative flex gap-3 p-4 rounded-lg border',
  {
    variants: {
      variant: {
        info: 'bg-blue-50 border-blue-200 text-blue-800',
        success: 'bg-success-50 border-green-200 text-green-800',
        warning: 'bg-warning-50 border-yellow-200 text-yellow-800',
        error: 'bg-error-50 border-red-200 text-red-800',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

// =============================================================================
// Icons
// =============================================================================

const iconMap = {
  info: Info,
  success: CheckCircle,
  warning: AlertCircle,
  error: XCircle,
};

// =============================================================================
// Component
// =============================================================================

export interface AlertProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  /** Alert title */
  title?: string;
  /** Dismiss handler (shows close button if provided) */
  onDismiss?: () => void;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = 'info', title, children, onDismiss, ...props }, ref) => {
    const Icon = iconMap[variant || 'info'];

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          alertVariants({ variant }),
          title ? 'items-start' : 'items-center',
          className
        )}
        {...props}
      >
        <Icon className={cn('h-5 w-5 flex-shrink-0', title && 'mt-0.5')} />
        <div className="flex-1">
          {title && <p className="font-medium">{title}</p>}
          {children && (
            <div className={cn('text-sm', title && 'mt-1')}>{children}</div>
          )}
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-1 rounded hover:bg-black/5 transition-colors"
            aria-label="Dismiss alert"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }
);

Alert.displayName = 'Alert';
